<template>
  <div class="tab-content-inner">
    <div class="info-alert info-alert--accent">
      <CIcon name="cil-star" class="info-icon" />
      <div>
        <strong>Avaliação de atendimentos:</strong> encerrado o atendimento, a IA lê a
        conversa — a transcrição da ligação ou as mensagens do WhatsApp — e devolve a nota
        geral, a percepção do cliente e um bloco por critério, citando trechos do que foi
        dito. O resultado aparece no histórico do cliente e no monitor de ligações.
      </div>
    </div>

    <!--
      O interruptor no mesmo desenho dos outros booleanos de configuração.
      Como linha solta ele não parecia clicável: a chave mais importante da
      tela passava por texto.
    -->
    <ToggleSwitch
      label="Avaliar atendimentos com IA"
      :ajuda="AJUDA_AVALIACAO"
      :value="!!parametro.avaliar_atendimento_ia"
      @input="update({ avaliar_atendimento_ia: $event })"
    />

    <div class="section-label">Critérios avaliados</div>
    <p class="section-help">
      Cada critério vira um bloco com nota própria. Sem nenhum cadastrado, vale o modelo
      marcado como padrão. A percepção do cliente é estimada sempre, independente dos
      critérios.
    </p>

    <!--
      Modelos prontos: escrever o primeiro critério do zero é a parte que
      trava. Quem supervisiona sabe o que cobra da equipe, mas não como dizer
      isso de um jeito que a IA julgue sempre igual.
    -->
    <div v-if="modelosProntos.length" class="modelos">
      <div class="modelos__lista">
        <button
          v-for="modelo in modelosProntos"
          :key="modelo.chave"
          type="button"
          class="modelo"
          @click="abrirPrevia(modelo)"
        >
          <span class="modelo__nome">
            {{ modelo.nome }}
            <span v-if="modelo.padrao" class="modelo__selo">Padrão</span>
          </span>
          <span class="modelo__resumo">{{ modelo.resumo }}</span>
          <span class="modelo__contagem">
            {{ modelo.criterios.length }} critérios
            <span v-if="modelo.calibragem"> · com calibragem</span>
          </span>
        </button>
      </div>
      <small class="modelos__ajuda">
        Clique para ver o modelo inteiro antes de aplicar.
      </small>
    </div>

    <div v-if="criterios.length" class="criterios">
      <div v-for="(criterio, indice) in criterios" :key="indice" class="criterio-item">
        <div class="criterio-ordem">{{ indice + 1 }}</div>
        <div class="criterio-campos">
          <input
            class="form-control criterio-titulo"
            placeholder="Nome do critério (ex.: Negociação e valor)"
            :value="criterio.titulo"
            @input="alterarCriterio(indice, { titulo: $event.target.value })"
          />
          <textarea
            class="form-control criterio-descricao"
            rows="2"
            placeholder="O que a IA deve observar neste critério. Quanto mais concreto, mais consistente fica a nota."
            :value="criterio.descricao"
            @input="alterarCriterio(indice, { descricao: $event.target.value })"
          ></textarea>
        </div>
        <CButton
          variant="outline"
          color="danger"
          size="sm"
          class="criterio-remover"
          title="Remover critério"
          aria-label="Remover critério"
          @click="removerCriterio(indice)"
        >
          <CIcon name="cil-trash" />
        </CButton>
      </div>
    </div>

    <div v-else class="sem-criterios">
      Nenhum critério próprio cadastrado — vale o modelo padrão.
    </div>

    <CButton variant="outline" color="primary" size="sm" @click="adicionarCriterio">
      <CIcon name="cil-plus" /> Adicionar critério
    </CButton>

    <div class="section-label">O que esta empresa valoriza no atendimento</div>
    <p class="section-help">
      Calibra as notas ao que importa aqui. A régua e o formato da resposta são fixos —
      este texto ajusta o julgamento, não a estrutura.
    </p>

    <textarea
      class="form-control prompt-textarea"
      :value="parametro.prompt_avaliacao_atendimento"
      @input="update({ prompt_avaliacao_atendimento: $event.target.value })"
      placeholder="Ex.: Trabalhamos com obra, então agilidade na entrega vale mais que desconto."
    ></textarea>

    <!--
      Ênfases prontas. Somam-se ao que já está escrito em vez de substituir:
      uma operação costuma valorizar mais de uma coisa, e o texto final é a
      junção delas, ajustada à mão.
    -->
    <div v-if="calibragensProntas.length" class="calibragens">
      <span class="calibragens__rotulo">Acrescentar uma ênfase:</span>
      <button
        v-for="calibragem in calibragensProntas"
        :key="calibragem.titulo"
        type="button"
        class="calibragem"
        :class="{ 'calibragem--usada': jaEstaNoTexto(calibragem) }"
        :title="calibragem.texto"
        :disabled="jaEstaNoTexto(calibragem)"
        @click="acrescentarCalibragem(calibragem)"
      >
        <CIcon :name="jaEstaNoTexto(calibragem) ? 'cil-check' : 'cil-plus'" />
        {{ calibragem.titulo }}
      </button>
    </div>

    <!--
      Prévia do modelo.

      Aplicar troca a lista inteira de critérios; sem ver o conteúdo antes, a
      escolha era pelo nome do cartão. O modal mostra o que vai entrar e deixa
      a decisão sobre a calibragem explícita, em vez de escondida num aviso.
    -->
    <modal name="previa-modelo-avaliacao" height="auto" width="760" :scrollable="true">
      <CloseModal :close="fecharPrevia" />
      <div class="modalbox" v-if="modeloEmPrevia">
        <div class="modalbox-content">
          <h5>{{ modeloEmPrevia.nome }}</h5>
          <p class="previa__resumo">{{ modeloEmPrevia.resumo }}</p>

          <div class="previa__secao">Critérios ({{ modeloEmPrevia.criterios.length }})</div>
          <ol class="previa__criterios">
            <li v-for="criterio in modeloEmPrevia.criterios" :key="criterio.titulo">
              <strong>{{ criterio.titulo }}</strong>
              <span>{{ criterio.descricao }}</span>
            </li>
          </ol>

          <template v-if="modeloEmPrevia.calibragem">
            <div class="previa__secao">Texto de calibragem</div>
            <p class="previa__calibragem">{{ modeloEmPrevia.calibragem }}</p>
          </template>

          <div class="previa__aviso">
            <CIcon name="cil-info" class="previa__aviso-icone" />
            <span>{{ avisoDaPrevia }}</span>
          </div>
        </div>
      </div>
      <div class="modal-footer-bar">
        <CButton variant="outline" color="secondary" @click="fecharPrevia">Cancelar</CButton>
        <CButton
          v-if="substituiCalibragem"
          variant="outline"
          color="primary"
          @click="confirmarPrevia(false)"
        >
          Só os critérios
        </CButton>
        <CButton color="primary" @click="confirmarPrevia(true)">
          {{ substituiCalibragem ? 'Substituir tudo' : 'Aplicar modelo' }}
        </CButton>
      </div>
    </modal>
  </div>
</template>

<script>
import { get } from "@/helpers/apiRequest";
import CloseModal from "@/components/CloseModal";
import ToggleSwitch from "@/components/ToggleSwitch.vue";

const MODAL = "previa-modelo-avaliacao";

/* A explicação da chave principal da tela, longa demais para o atributo. */
const AJUDA_AVALIACAO =
  "Vale para os dois canais: a ligação, a partir da transcrição que o softphone "
  + "envia, e a conversa escrita. A nota é gravada no atendimento do cliente, e é "
  + "por isso que a chave fica aqui, e não em cada canal. Desligado, nada é "
  + "avaliado e nada é cobrado do provedor.";

export default {
  name: "tab-avaliacao-atendimento",
  components: { CloseModal, ToggleSwitch },
  props: {
    parametro: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      AJUDA_AVALIACAO,
      modelosProntos: [],
      calibragensProntas: [],
      modeloEmPrevia: null,
    };
  },
  computed: {
    criterios() {
      return Array.isArray(this.parametro.criterios_avaliacao_atendimento)
        ? this.parametro.criterios_avaliacao_atendimento
        : [];
    },

    calibragemAtual() {
      return (this.parametro.prompt_avaliacao_atendimento || "").trim();
    },

    /* Só há decisão a tomar quando as duas pontas têm texto. */
    substituiCalibragem() {
      return !!this.calibragemAtual && !!(this.modeloEmPrevia && this.modeloEmPrevia.calibragem);
    },

    avisoDaPrevia() {
      if (!this.modeloEmPrevia) return "";

      const partes = [];

      if (this.criterios.length) {
        partes.push(
          `Os ${this.criterios.length} critérios atuais serão trocados pelos ${this.modeloEmPrevia.criterios.length} acima.`
        );
      }

      if (this.substituiCalibragem) {
        partes.push("O texto de calibragem já escrito também será substituído, a menos que você escolha trazer só os critérios.");
      }

      partes.push("Depois de aplicado tudo continua editável, e nada é gravado até você salvar.");

      return partes.join(" ");
    },
  },
  methods: {
    update(changes) {
      this.$emit("update", changes);
    },
    // A lista inteira é reemitida a cada tecla porque o pai guarda o parâmetro completo
    // e salva de uma vez — não há edição parcial de critério no servidor.
    salvarCriterios(criterios) {
      this.update({ criterios_avaliacao_atendimento: criterios });
    },
    alterarCriterio(indice, mudancas) {
      const criterios = this.criterios.map((criterio, posicao) =>
        posicao === indice ? { ...criterio, ...mudancas } : criterio
      );
      this.salvarCriterios(criterios);
    },
    adicionarCriterio() {
      this.salvarCriterios([...this.criterios, { titulo: "", descricao: "" }]);
    },
    removerCriterio(indice) {
      this.salvarCriterios(this.criterios.filter((_, posicao) => posicao !== indice));
    },

    carregarModelos() {
      get("/admin/avaliacao-atendimento/modelos")
        .then((resposta) => {
          const dados = (resposta && resposta.data) || {};

          this.modelosProntos = dados.modelos || [];
          this.calibragensProntas = dados.calibragens || [];
        })
        .catch(() => {
          // Sem os modelos a tela continua inteira: eles economizam digitação,
          // não habilitam nada.
          this.modelosProntos = [];
          this.calibragensProntas = [];
        });
    },

    abrirPrevia(modelo) {
      this.modeloEmPrevia = modelo;
      this.$modal.show(MODAL);
    },

    fecharPrevia() {
      this.$modal.hide(MODAL);
      this.modeloEmPrevia = null;
    },

    /*
     * Aplicar é copiar: os critérios do modelo viram os critérios do cliente e
     * ficam editáveis como qualquer outro. Não há vínculo depois disso.
     */
    confirmarPrevia(comCalibragem) {
      const modelo = this.modeloEmPrevia;

      if (!modelo) return;

      const mudancas = {
        criterios_avaliacao_atendimento: modelo.criterios.map((criterio) => ({
          titulo: criterio.titulo,
          descricao: criterio.descricao,
        })),
      };

      if (comCalibragem && modelo.calibragem) {
        mudancas.prompt_avaliacao_atendimento = modelo.calibragem;
      }

      this.update(mudancas);
      this.fecharPrevia();
    },

    jaEstaNoTexto(calibragem) {
      return this.calibragemAtual.indexOf(calibragem.texto) !== -1;
    },

    acrescentarCalibragem(calibragem) {
      const atual = this.calibragemAtual;

      this.update({
        prompt_avaliacao_atendimento: atual ? `${atual}\n\n${calibragem.texto}` : calibragem.texto,
      });
    },
  },
  mounted() {
    this.carregarModelos();
  },
};
</script>

<style scoped>
.tab-content-inner {
  padding: 20px;
}

.info-alert {
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  background: #edf8f9;
  border: 1px solid #c5e6e9;
  border-radius: 8px;
  font-size: 13px;
  color: #344054;
  line-height: 1.5;
  margin-bottom: 16px;
}

.info-icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: #2298a3;
}

.section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #667085;
  letter-spacing: 0.5px;
  margin-top: 28px;
  margin-bottom: 4px;
}

.section-help {
  font-size: 12px;
  color: #667085;
  line-height: 1.5;
  max-width: 820px;
  margin-bottom: 12px;
}

/* Grade, e não flex: com colunas de largura igual os cartões ficam do mesmo
   tamanho, e o rodapé de cada um alinha com o dos vizinhos. */
.modelos__lista {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
}

.modelos__ajuda {
  display: block;
  font-size: 11px;
  color: #98a2b3;
  margin-top: 6px;
}

.modelo {
  display: flex;
  flex-direction: column;
  gap: 2px;
  height: 100%;
  text-align: left;
  padding: 10px 14px;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
}

.modelo:hover {
  border-color: #3a3e81;
  background: #f7f7fb;
}

.modelo__nome {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #344054;
}

.modelo__selo {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #17727b;
  background: #edf8f9;
  border-radius: 10px;
  padding: 1px 8px;
}

.modelo__resumo {
  font-size: 12px;
  color: #667085;
  line-height: 1.45;
}

.modelo__contagem {
  font-size: 11px;
  color: #98a2b3;
  margin-top: auto;
  padding-top: 6px;
}

/* Critérios */
.criterios {
  margin: 16px 0 10px;
}

.criterio-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  margin-bottom: 8px;
  background: #fff;
}

.criterio-ordem {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #eceaf3;
  color: #3a3e81;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 4px;
}

.criterio-campos {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.criterio-titulo {
  font-weight: 600;
}

.criterio-descricao {
  resize: vertical;
}

.criterio-remover {
  flex-shrink: 0;
  margin-top: 2px;
}

.sem-criterios {
  padding: 14px 16px;
  margin: 16px 0 10px;
  border: 1px dashed #d0d5dd;
  border-radius: 10px;
  font-size: 13px;
  color: #667085;
}

.prompt-textarea {
  min-height: 120px;
  resize: vertical;
}

/* Ênfases prontas para a calibragem */
.calibragens {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
}

.calibragens__rotulo {
  font-size: 12px;
  color: #667085;
  margin-right: 2px;
}

.calibragem {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  padding: 4px 10px;
  border: 1px solid #e4e7eb;
  border-radius: 16px;
  background: #fff;
  color: #344054;
  cursor: pointer;
  transition: all 0.15s ease;
}

.calibragem:hover {
  border-color: #3a3e81;
  background: #f7f7fb;
}

.calibragem--usada,
.calibragem--usada:hover {
  color: #17727b;
  background: #edf8f9;
  border-color: #c5e6e9;
  cursor: default;
}

/* Prévia. O teto de altura e a rolagem do corpo vêm do estilo comum das
   modais (components.css): o modelo de doze critérios passa da altura da tela
   e rola por dentro, mantendo o rodapé à vista. */
.previa__resumo {
  font-size: 13px;
  color: #667085;
  margin-bottom: 18px;
}

.previa__secao {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #667085;
  margin-bottom: 8px;
}

/* O modelo mais longo tem doze critérios, e com recuo de um dígito o "1" de
   10, 11 e 12 saía para fora da caixa. */
.previa__criterios {
  margin: 0 0 18px;
  padding-left: 26px;
}

.previa__criterios li {
  font-size: 13px;
  color: #344054;
  margin-bottom: 8px;
  line-height: 1.45;
}

.previa__criterios strong {
  display: block;
}

.previa__criterios span {
  color: #667085;
  font-size: 12px;
}

.previa__calibragem {
  font-size: 12px;
  color: #667085;
  line-height: 1.6;
  white-space: pre-line;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 18px;
}

.previa__aviso {
  display: flex;
  gap: 8px;
  font-size: 12px;
  color: #344054;
  line-height: 1.5;
  background: #edf8f9;
  border: 1px solid #c5e6e9;
  border-radius: 8px;
  padding: 10px 14px;
}

.previa__aviso-icone {
  flex-shrink: 0;
  margin-top: 2px;
  color: #2298a3;
}

.modal-footer-bar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #e9ecef;
  background: #fff;
}
</style>
